// ─── El registro de auditoría (la bitácora) ──────────────────────────────────
// Toda fila de `public.auditoria` sale de aquí: los guardados de las entidades
// (registrarAuditoria, desde saveData), los cambios de configuración
// (registrarCambioConfig, desde la sincronización de tenant_config) y los
// eventos de proceso (registrarEvento: emitir ante la DIAN, reenviar el
// correo, abrir el PDF, entrar a AURA, administrar usuarios…).
//
// La tabla es de solo inserción y lectura (RLS sin update ni delete): lo que
// queda escrito no se puede cambiar desde la app. La lectura en palabras vive
// en src/core/bitacora.js.
import { aligerar, referenciaDe, sinSecretos } from "./bitacora.js";
import { state } from "./state.js";
import { cloudState, supa } from "./supabaseClient.js";

function quien() {
  return { usuario_email: state.usuarioEmail || null, usuario_nombre: state.usuarioNombre || null };
}

function insertar(filas, que) {
  if (!filas.length || !cloudState.tenantId) return Promise.resolve();
  return Promise.resolve(supa.from("auditoria").insert(filas)).then((r) => {
    if (r && r.error) console.error(`⚠️ Error registrando auditoría de ${que}:`, r.error.message);
  }).catch((e) => console.error(`⚠️ Error registrando auditoría de ${que}:`, e && e.message));
}

export function registrarAuditoria(key, agregadosOModificados, removidos, prevArr, idField) {
  if (!cloudState.tenantId) return;
  const prevPorId = new Map((prevArr || []).map(p => [p[idField], p]));
  const filas = [];
  agregadosOModificados.forEach(actual => {
    const anterior = prevPorId.get(actual[idField]);
    filas.push({
      tenant_id: cloudState.tenantId, entidad: key, entidad_id: String(actual[idField]),
      accion: anterior ? "update" : "create", ...quien(),
      referencia: referenciaDe(key, actual) || referenciaDe(key, anterior),
      datos_antes: anterior ? aligerar(anterior) : null, datos_despues: aligerar(actual)
    });
  });
  removidos.forEach(anterior => {
    filas.push({
      tenant_id: cloudState.tenantId, entidad: key, entidad_id: String(anterior[idField]),
      accion: "delete", ...quien(), referencia: referenciaDe(key, anterior),
      datos_antes: aligerar(anterior), datos_despues: null
    });
  });
  return insertar(filas, key);
}

// Un proceso que no es un guardado. `detalle` va en datos_despues (lo que hace
// falta para entenderlo después: el CUFE, el correo, el motivo del rechazo).
export function registrarEvento(entidad, entidadId, evento, opts = {}) {
  if (!cloudState.tenantId) return Promise.resolve();
  return insertar([{
    tenant_id: cloudState.tenantId, entidad, entidad_id: String(entidadId || "—"),
    accion: "evento", evento, ...quien(),
    referencia: opts.referencia != null ? String(opts.referencia).slice(0, 200) : null,
    datos_antes: null, datos_despues: opts.detalle ? aligerar(opts.detalle) : null
  }], `${entidad}/${evento}`);
}

// Un cambio de configuración (tenant_config). Se compara con el último valor
// conocido de la nube para no dejar una fila por cada guardado idéntico.
export function registrarCambioConfig(clave, antes, despues) {
  if (!cloudState.tenantId) return Promise.resolve();
  antes = sinSecretos(antes); despues = sinSecretos(despues);
  if (JSON.stringify(antes ?? null) === JSON.stringify(despues ?? null)) return Promise.resolve();
  const envolver = (v) => (v && typeof v === "object" && !Array.isArray(v)) ? { ...v, __clave: clave }
    : v == null ? null : { valor: v, __clave: clave };
  const a = envolver(antes), d = envolver(despues);
  return insertar([{
    tenant_id: cloudState.tenantId, entidad: "configuracion", entidad_id: clave,
    accion: antes == null ? "create" : "update", ...quien(),
    referencia: referenciaDe("configuracion", d || a),
    datos_antes: a ? aligerar(a) : null, datos_despues: d ? aligerar(d) : null
  }], `configuración ${clave}`);
}

// La salida se anota ANTES de cerrar: sin sesión ya no hay permiso de escribir.
// Con tope de 2 s: anotarla nunca puede impedir salir.
export async function anotarSalida(evento = "salida") {
  try {
    await Promise.race([registrarEvento("sesion", state.usuarioEmail || "—", evento, { referencia: state.usuarioEmail }),
      new Promise(r => setTimeout(r, 2000))]);
  } catch { /* se sale igual */ }
}
